import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FavoritesItem } from './FavoritesItem';
import { type FavoritesProviderChildrenProps } from './FavoritesProviderChildrenProps';
import { type FavoritesItemPropsType } from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { TILE_SIZES, type TileSize } from 'dibs-buyer-product-tile/exports/BuyerProductTile';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

import { type SharedFavoriteItem_item$key } from './__generated__/SharedFavoriteItem_item.graphql';

import styles from './SharedFavoriteItem.scss';

const itemFragment = graphql`
    fragment SharedFavoriteItem_item on Item {
        isNewListing
        ...FavoritesItem_item
    }
`;

type Props = {
    item: SharedFavoriteItem_item$key | null | undefined;
    isSponsored?: boolean;
    favorites?: FavoritesProviderChildrenProps;
    fetchFolderOnMouseOver?: boolean;
    showFavoritesTooltip?: boolean;
    showNudge?: boolean;
    tileSize: TileSize;
} & FavoritesItemPropsType;

export const SharedFavoriteItem: FC<Props> = ({
    item: itemRef,
    isFolderVisible,
    isSponsored,
    favorites,
    fetchFolderOnMouseOver,
    showFavoritesTooltip,
    showNudge,
    tileSize,
    ...favoritesItemProps
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { isItemTileRedesignTestVariant, isMobile } = useServerVarsContext();
    const applyIconWrapperClass = isMobile || !fetchFolderOnMouseOver;
    if (!favorites) {
        return null;
    }
    let favoritesItemTheme;
    if (isItemTileRedesignTestVariant && tileSize === TILE_SIZES.small) {
        favoritesItemTheme = 'circle-frosted-small' as const;
    } else if (isItemTileRedesignTestVariant && tileSize !== TILE_SIZES.small) {
        favoritesItemTheme = 'circle-frosted-medium' as const;
    }
    return (
        <FavoritesItem
            isNewListing={item?.isNewListing || false}
            isSponsored={isSponsored}
            wrapperClass={classnames(styles.favoritesWrapper, {
                [styles.isItemTileRedesignTestVariant]: isItemTileRedesignTestVariant,
            })}
            heartIconWrapperClass={classnames(styles.heartIconWrapperSpacing, {
                [styles.reducedIcon]: applyIconWrapperClass && !isItemTileRedesignTestVariant,
                [styles.isItemTileRedesignTestVariant]: isItemTileRedesignTestVariant,
            })}
            folderIconWrapperClass={classnames(styles.folderWrapper, {
                [styles.reducedIcon]: applyIconWrapperClass && !isItemTileRedesignTestVariant,
                [styles.isItemTileRedesignTestVariant]: isItemTileRedesignTestVariant,
            })}
            item={item}
            {...favorites}
            fetchFolder={favorites.fetchFolder && (!fetchFolderOnMouseOver || !!isFolderVisible)}
            showTooltip={showFavoritesTooltip}
            showNudge={showNudge}
            theme={favoritesItemTheme}
            {...favoritesItemProps}
        />
    );
};
